import { ReactElement, ReactNode } from 'react';
import clsx from 'clsx';

import { formatDateString } from 'src/utils/date';
import { beautifyEnum } from 'src/utils/utility';
import { CustomTooltip } from 'src/presentations/components/atom/customtooltip';
import IconInfo from 'src/assets/images/icons/info-circle-grey.svg';
import IconBuilding from './icons/Building';
import IconAnchor from './icons/Anchor';
import IconAeroplane from './icons/AeroplaneSquare';
import IconHouse from './icons/House';
import IconCourier from './icons/Courier';

const styles = {
  start: {
    wrapper: 'items-start',
    title: 'text-[#16AD89]',
    titleDisabled: 'text-[#bfbfbf]',
    icon: 'border-[#16AD89] bg-[#E0F3E6]',
    iconDisabled: 'border-[#bfbfbf] bg-white',
    date: 'text-left',
  },
  end: {
    wrapper: 'items-end',
    title: 'text-[#E72547] text-right',
    titleDisabled: 'text-[#bfbfbf] text-right',
    icon: 'border-[#E72547] bg-[#FCE5E9]',
    iconDisabled: 'border-[#bfbfbf] bg-white',
    date: 'text-right',
  },
  center: {
    wrapper: 'items-center',
    title: 'text-[#475B69] text-center',
    titleDisabled: 'text-[#bfbfbf] text-center',
    icon: 'border-[#6A889C] bg-[#475B69]',
    iconDisabled: 'border-[#bfbfbf] bg-white',
    date: 'font-normal items-center flex justify-center',
  },
};

interface ITransportLocationProps {
  title: string;
  date: string;
  dateType?: string;
  tooltipContent?: ReactNode;
  disabled?: boolean;
  position: 'start' | 'end' | 'center';
  transportMedium: string;
}

const TransportLocation: React.FC<ITransportLocationProps> = (props) => {
  const { title, date, dateType, position = 'center', disabled, transportMedium, tooltipContent } = props;
  const formattedDate = formatDateString(date);

  // Determine the displayed title and the tooltip text
  const displayTitle = disabled ? '-' : beautifyEnum(title);
  const tooltipText = tooltipContent || beautifyEnum(title);

  /**
   * getIcon
   */
  const getIcon = (): ReactElement => {
    if (position === 'start') {
      return <IconBuilding color={disabled ? '#bfbfbf' : '#16AD89'} />;
    }
    if (position === 'end') {
      return <IconBuilding color={disabled ? '#bfbfbf' : '#E72547'} />;
    }
    if (transportMedium === 'AIR') {
      return <IconAeroplane color={disabled ? '#bfbfbf' : '#ffffff'} size={24} />;
    }
    if (transportMedium === 'ROAD') {
      return <IconHouse color={disabled ? '#bfbfbf' : '#ffffff'} size={24} />;
    }
    if (transportMedium === 'CT') {
      return <IconAeroplane color={disabled ? '#bfbfbf' : '#ffffff'} size={24} />;
    }
    if (transportMedium === 'COURIER') {
      return <IconCourier color={disabled ? '#bfbfbf' : '#ffffff'} size={24} />;
    }
    return <IconAnchor color={disabled ? '#bfbfbf' : '#ffffff'} />;
  };

  return (
    <div className={clsx('flex flex-col w-42 xl:w-48', styles[position].wrapper)}>
      {tooltipContent ? (
        <div className='flex items-center justify-center gap-6 mb-12 w-100'>
          <h3 className={clsx('text-12 xl:text-14 leading-18 h-18 truncate uppercase', disabled ? styles[position].titleDisabled : styles[position].title)}>
            {displayTitle}
          </h3>

          {!disabled && (
            <CustomTooltip title={<span className='uppercase'>{tooltipText}</span>}>
              <img width={20} src={IconInfo} alt='' />
            </CustomTooltip>
          )}
        </div>
      ) : (
        <CustomTooltip title={<span className='uppercase'>{tooltipText}</span>}>
          <h3
            className={clsx(
              'text-12 xl:text-14 leading-16 xl:leading-18 h-16 xl:h-18 mb-12 w-100 truncate uppercase',
              disabled ? styles[position].titleDisabled : styles[position].title
            )}
          >
            {displayTitle}
          </h3>
        </CustomTooltip>
      )}
      <div
        className={clsx(
          'w-42 xl:w-48 h-42 xl:h-48 rounded-full border-2 mb-12 flex items-center justify-center',
          disabled ? styles[position].iconDisabled : styles[position].icon
        )}
      >
        {getIcon()}
      </div>
      <p className={clsx('text-12 xl:text-14 leading-18 h-18 w-100', styles[position].date)}>{disabled ? '-' : formattedDate}</p>
      {dateType && <div className={clsx('mt-4 text-10 text-[#9f9f9f] uppercase w-80', styles[position].date)}>{dateType}</div>}
    </div>
  );
};

export default TransportLocation;
